import { supabase } from '../supabase';
import { Invite, InviteFormData } from '../../types/invite';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

export async function fetchWithRetry<T>(
  operation: () => Promise<T>,
  retries = MAX_RETRIES
): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    if (
      retries > 0 &&
      error instanceof Error &&
      error.message.includes('Failed to fetch')
    ) {
      console.log(`Network error, retrying... (${retries} attempts remaining)`);
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
      return fetchWithRetry(operation, retries - 1);
    }
    throw error;
  }
}

export async function getRemainingInvites(): Promise<number> {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('Not authenticated');

    const { data: userData, error: userDataError } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', user.id)
      .single();

    if (userDataError) throw userDataError;
    if (!userData) throw new Error('User not found');

    const { data, error } = await supabase
      .from('invite_allocations')
      .select('remaining_invites')
      .eq('user_id', userData.id)
      .single();

    if (error) throw error;
    return data?.remaining_invites || 0;
  } catch (error) {
    console.error('Error getting remaining invites:', error);
    throw error;
  }
}

export async function sendInvite(inviteData: InviteFormData[]): Promise<Invite[]> {
  try {
    if (!inviteData?.length) {
      throw new Error('No invite data provided');
    }

    // Validate each invite
    inviteData.forEach((invite) => {
      if (!invite.name?.trim()) {
        throw new Error('Name is required for all invites');
      }
      if (!invite.email?.trim()) {
        throw new Error('Email is required for all invites');
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invite.email)) {
        throw new Error(`Invalid email format: ${invite.email}`);
      }
    });

    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('Not authenticated');

    const { data: userData, error: userDataError } = await supabase
      .from('users')
      .select('id, full_name')
      .eq('auth_id', user.id)
      .single();

    if (userDataError) throw userDataError;
    if (!userData) throw new Error('User not found');

    const inviterName = userData.full_name;

    const { data, error } = await supabase
      .from('user_invites')
      .insert(
        inviteData.map((invite) => ({
          inviter_id: userData.id,
          inviter_full_name: inviterName,
          name: invite.name,
          invitee_email: invite.inviteMethod === 'email' ? invite.email : null,
          phone_number: invite.inviteMethod === 'whatsapp' ? invite.phoneNumber : null,
          country_code: invite.inviteMethod === 'whatsapp' ? invite.countryCode : null,
          invite_method: invite.inviteMethod,
        }))
      )
      .select()
      .order('created_at', { ascending: false });

    if (error) {
      if (error.message.includes('remaining_invites')) {
        throw new Error('No remaining invites available');
      } else if (error.message.includes('duplicate key')) {
        throw new Error('One or more emails have already been invited');
      } else if (error.message.includes('violates check constraint')) {
        throw new Error('Invalid invite data provided');
      }
      throw error;
    }

    if (!data?.length) {
      throw new Error('Failed to create invites');
    }

    return data.map((invite) => ({
      id: invite.id,
      inviterId: invite.inviter_id,
      name: invite.name,
      inviteeEmail: invite.invitee_email,
      inviteMethod: invite.invite_method,
      status: invite.status,
      createdAt: invite.created_at,
      expiresAt: invite.expires_at,
      acceptedAt: invite.accepted_at,
    }));
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Failed to send invites';
    console.error('Error sending invites:', { error: message });
    throw error;
  }
}

export async function getInvites(): Promise<Invite[]> {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('Not authenticated');

    const { data: userData, error: userDataError } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', user.id)
      .single();

    if (userDataError) throw userDataError;
    if (!userData) throw new Error('User not found');

    const { data, error } = await supabase
      .from('user_invites')
      .select('*')
      .eq('inviter_id', userData.id)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data.map((invite) => ({
      id: invite.id,
      inviterId: invite.inviter_id,
      inviteeEmail: invite.invitee_email,
      inviteMethod: invite.invite_method,
      status: invite.status,
      createdAt: invite.created_at,
      expiresAt: invite.expires_at,
      acceptedAt: invite.accepted_at,
    }));
  } catch (error) {
    console.error('Error getting invites:', error);
    throw error;
  }
}

export function subscribeToInvites(callback: (invite: Invite) => void) {
  const subscription = supabase
    .channel('invite_changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'user_invites',
      },
      async (payload) => {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;

          const { data: userData } = await supabase
            .from('users')
            .select('id')
            .eq('auth_id', user.id)
            .single();

          if (!userData) return;

          if (payload.new.inviter_id === userData.id) {
            callback({
              id: payload.new.id,
              inviterId: payload.new.inviter_id,
              inviteeEmail: payload.new.invitee_email,
              inviteMethod: payload.new.invite_method,
              status: payload.new.status,
              createdAt: payload.new.created_at,
              expiresAt: payload.new.expires_at,
              acceptedAt: payload.new.accepted_at,
            });
          }
        } catch (error) {
          console.error('Error processing invite update:', error);
        }
      }
    )
    .subscribe();

  return () => {
    subscription.unsubscribe();
  };
}

// NEW: validateInvite helper and associated type
interface ValidateInviteResult {
  valid: boolean;
  inviter_id: string;
  inviter_full_name: string;
  name: string | null;
  status: string;
  expires_at: string;
  avatar_url: string | null;
  invite_code: string;
  message?: string;
}

export async function validateInvite(
  code: string,
  inviteeEmail?: string
): Promise<ValidateInviteResult | null> {
  try {
    const { data, error } = await supabase.rpc('validate_invite', {
      p_invite_code: code,
      p_invitee_email: inviteeEmail ?? null,
    });
    
    if (error) {
      console.error('Invite validation error:', error);
      // Extract the specific error message from the backend
      const errorMessage = error.message || 'Failed to validate invite';
      
      // Check for specific error conditions in the message
      if (errorMessage.includes('already used')) {
        throw new Error('This invite code has already been used');
      } else if (errorMessage.includes('expired')) {
        throw new Error('This invite code has expired');
      } else if (errorMessage.includes('not found')) {
        throw new Error('Invalid invite code');
      } else {
        throw new Error(errorMessage);
      }
    }
    
    if (!data || data.length === 0) {
      return {
        valid: false,
        inviter_id: '',
        inviter_full_name: '',
        name: null,
        status: 'invalid',
        expires_at: '',
        avatar_url: null,
        invite_code: code,
        message: 'Invalid invite code'
      };
    }
    
    return data[0];
  } catch (error) {
    console.error('Error in validateInvite:', error);
    // For network errors, return null instead of throwing
    if (error instanceof Error && error.message.includes('Failed to fetch')) {
      return null;
    }
    throw error;
  }
}

/**
 * NEW: generateInviteLink
 *
 * This function retrieves the current user's info and then calls the 
 * 'create_invite' RPC (with p_invite_method set to 'link') to generate a unique invite.
 */
export async function generateInviteLink(name: string, inviteeEmail?: string) {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  if (userError) throw userError;
  if (!user) throw new Error('Not authenticated');

  const { data: userData, error: userDataError } = await supabase
    .from('users')
    .select('id, full_name')
    .eq('auth_id', user.id)
    .single();
  if (userDataError) throw userDataError;
  if (!userData) throw new Error('User not found');

  const expiresAt = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();

  const { data, error } = await supabase.rpc("create_invite", {
    p_inviter_id: userData.id,
    p_invite_method: "link",
    p_name: name,
    p_expires_at: expiresAt,
    p_invitee_email: inviteeEmail ?? null,
    p_phone_number: null,
    p_country_code: null,
    p_inviter_full_name: userData.full_name,
  });
  if (error) throw error;
  return data[0]; // Expecting one row as response.
}
